import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";

import { Checkbox } from "@/design-system/primitives/checkbox";
import { ScrollArea } from "@/design-system/primitives/scroll-area";
import type { IndexedDistrict } from "@/districts/district.model.ts";
import type { DistrictSet } from "@/districts/district.model.ts";
import { cn } from "@/design-system/cn.ts";

const SETS: { id: DistrictSet; label: string }[] = [
  { id: "neighborhood", label: "Neighbourhoods" },
  { id: "supervisor", label: "Districts" },
];

export function DistrictPicker({
  districts,
  set,
  onSetChange,
  selected,
  onToggle,
  onSelectNone,
  counts,
}: {
  districts: IndexedDistrict[];
  set: DistrictSet;
  onSetChange: (set: DistrictSet) => void;
  selected: string[];
  onToggle: (id: string) => void;
  onSelectNone: () => void;
  /** How many currently-visible listings fall in each district. */
  counts: Map<string, number>;
}) {
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return districts
      .filter((d) => d.props.set === set)
      .filter((d) => q === "" || d.name.toLowerCase().includes(q))
      .toSorted((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [districts, set, query]);

  const selectedSet = new Set(selected);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 rounded-lg border border-rule bg-ink-900/60 p-0.5">
        {SETS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onSetChange(s.id)}
            className={cn(
              "flex-1 rounded-[6px] px-2 py-1.5 font-mono text-[9px] font-medium uppercase tracking-[0.12em] transition",
              set === s.id
                ? "bg-marine-ink/12 text-marine-ink shadow-[inset_0_0_0_1px_var(--color-marine)]/30"
                : "text-chalk-mute hover:text-chalk-dim",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-chalk-mute" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name…"
          aria-label="Filter districts by name"
          className="w-full rounded-lg border border-rule bg-ink-900/60 py-1.5 pl-8 pr-7 text-[12.5px] text-chalk outline-none transition placeholder:text-chalk-mute focus:border-rule-bright"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear filter"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-chalk-mute hover:text-chalk"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <ScrollArea className="h-52 rounded-lg border border-rule bg-ink-900/40">
        <div className="p-1">
          {rows.length === 0 && (
            <p className="px-2 py-6 text-center text-[12px] text-chalk-mute">No match.</p>
          )}
          {rows.map((d) => {
            const checked = selectedSet.has(d.id);
            const n = counts.get(d.id) ?? 0;
            return (
              <label
                key={d.id}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition",
                  checked ? "bg-marine-ink/10" : "hover:bg-ink-700/60",
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(d.id)}
                  className="size-3.5 rounded-[3px] border-rule-bright data-[state=checked]:border-marine-ink data-[state=checked]:bg-marine-ink data-[state=checked]:text-on-accent"
                />
                <span
                  className={cn(
                    "flex-1 truncate text-[12.5px] leading-tight",
                    checked ? "text-chalk" : "text-chalk-dim",
                  )}
                >
                  {d.name}
                </span>
                <span
                  className={cn(
                    "tnum font-mono text-[10px]",
                    n === 0 ? "text-chalk-mute/50" : "text-chalk-mute",
                  )}
                >
                  {n}
                </span>
              </label>
            );
          })}
        </div>
      </ScrollArea>

      <div className="flex items-center justify-between">
        <span className="eyebrow">{selected.length} selected</span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={onSelectNone}
            className="font-mono text-[9px] uppercase tracking-[0.12em] text-chalk-mute underline-offset-2 transition hover:text-chalk hover:underline"
          >
            Deselect all
          </button>
        )}
      </div>
    </div>
  );
}
